import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {memo, type JSX} from 'react'
import {useRouter} from 'next/router'
import type {Block} from '@/services/kontent-ai/types'
import type {BazaarVoiceRatingsContentItem} from '@/products/ratings'
import {BazaarVoiceRatingsBlock} from '@/products/ratings'
import type {SkuProviderContentItem} from '@/products/sku-provider'
import {SkuProviderBlock} from '@/products/sku-provider'
import type {PearCommerceWidgetContentItem} from '@/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import {PearCommerceWidget} from '@/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import type {PDPHeaderAccordionContentItem} from '@/products/pdp/pdp-accordion/pdp-accordion'
import {PDPHeaderAccordionBlock} from '@/products/pdp/pdp-accordion/pdp-accordion'
import type {PDPPopupRebatesContentItem} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import {PDPPopupRebatesBlock} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import type {PDPPopupWithRichTextContentItem} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import {PDPPopupWithRichTextBlock} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import type {PDPSliderContentItem} from '@/products/flexible-web-toolkit/blocks/slider'
import {SliderBlock} from '@/products/flexible-web-toolkit/blocks/slider'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {TextButton} from '../../flexible-web-toolkit/components/button/text-button'
import {
	PDPFindAVetCard,
	type PDPFindAVetCardContentItem,
} from '../pdp-find-a-vet-card/pdp-find-a-vet-card'
import {getBrandColorClasses} from '../utils/brand-colors'
import {
	ReusableCTAButtonComponent,
	type CtaButtonContentItem,
} from '../../flexible-web-toolkit/blocks/cta-button'
import {
	MikMakButtonBlock,
	type MikmakButtonContentItem,
} from '../../where-to-buy/mikmak/mikmak-button'

export type PDPHeaderBlockContentItem = IContentItem<{
	slider: Elements.LinkedItemsElement<PDPSliderContentItem>
	title: Elements.TextElement
	productLabelText: Elements.TextElement
	productLabelUrl: Elements.TextElement
	safetyDataText: Elements.TextElement
	safetyDataUrl: Elements.TextElement
	description: Elements.TextElement
	descriptionRichText?: Elements.RichTextElement
	ratings: Elements.LinkedItemsElement<BazaarVoiceRatingsContentItem>
	skuProvider: Elements.LinkedItemsElement<SkuProviderContentItem>
	pearCommerce: Elements.LinkedItemsElement<PearCommerceWidgetContentItem>
	pdpHeaderAccordion: Elements.LinkedItemsElement<PDPHeaderAccordionContentItem>
	pdpPopupRebates: Elements.LinkedItemsElement<PDPPopupRebatesContentItem>
	pdpPopupRichText: Elements.LinkedItemsElement<PDPPopupWithRichTextContentItem>
	pdpWithFindAVetCard: Elements.LinkedItemsElement<PDPFindAVetCardContentItem>
	buyOnlineCta: Elements.LinkedItemsElement<MikmakButtonContentItem>
	findVetCta: Elements.LinkedItemsElement<CtaButtonContentItem>
}>

type PDPHeaderDescription =
	| Elements.RichTextElement
	| Elements.TextElement
	| string
	| null

const isRichTextElement = (
	value: PDPHeaderDescription | undefined
): value is Elements.RichTextElement => {
	return Boolean(
		value &&
		typeof value === 'object' &&
		'links' in value &&
		'images' in value &&
		'linkedItemCodenames' in value
	)
}

export const getPdpHeaderDescriptionContent = ({
	description,
	descriptionRichText,
}: {
	description?: Elements.TextElement | string | null
	descriptionRichText?: Elements.RichTextElement | Elements.TextElement | null
}): PDPHeaderDescription => {
	if (
		descriptionRichText &&
		'value' in descriptionRichText &&
		descriptionRichText.value.trim()
	) {
		return descriptionRichText as PDPHeaderDescription
	}

	if (typeof description === 'string') {
		return description
	}

	if (description && 'value' in description) {
		return description
	}

	return null
}

const getPdpHeaderDescriptionText = (
	description: PDPHeaderDescription | undefined
): string | null => {
	if (typeof description === 'string') {
		return description.trim() || null
	}

	if (!description || !('value' in description)) {
		return null
	}

	const text = description.value
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()

	return text || null
}

export const hasPdpHeaderDescriptionContent = ({
	description,
	descriptionRichText,
}: {
	description?: Elements.TextElement | string | null
	descriptionRichText?: Elements.RichTextElement | Elements.TextElement | null
}): boolean => {
	if (
		descriptionRichText &&
		'value' in descriptionRichText &&
		descriptionRichText.value.trim()
	) {
		return true
	}

	if (typeof description === 'string') {
		return description.trim().length > 0
	}

	if (description && 'value' in description) {
		return description.value.trim().length > 0
	}

	return false
}

const PDPHeaderBlockComponent: Block<PDPHeaderBlockContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	// Destructure elements with explicit types for clarity
	const {
		slider,
		title,
		productLabelText,
		productLabelUrl,
		safetyDataText,
		safetyDataUrl,
		description,
		descriptionRichText,
		ratings,
		skuProvider,
		pearCommerce,
		pdpHeaderAccordion,
		pdpPopupRebates,
		pdpPopupRichText,
		pdpWithFindAVetCard,
		buyOnlineCta,
		findVetCta,
	} = block.elements

	const {colorId} = colorWithFallback({
		pageConfig: page.elements.config[0],
	})

	const brandColorClasses = getBrandColorClasses(colorId)

	const {locale = 'us'} = useRouter()
	const selectedDescription = getPdpHeaderDescriptionContent({
		description,
		descriptionRichText: descriptionRichText as
			| Elements.RichTextElement
			| Elements.TextElement
			| null
			| undefined,
	})
	const isUsLocale = locale === 'us'
	const richTextDescription =
		selectedDescription && isRichTextElement(selectedDescription)
			? selectedDescription
			: null
	const hasDescription =
		selectedDescription !== null &&
		(richTextDescription !== null ||
			getPdpHeaderDescriptionText(selectedDescription) !== null)

	const renderDescriptionContent = (): JSX.Element | null => {
		if (typeof selectedDescription === 'string') {
			return <p className="whitespace-pre-line">{selectedDescription}</p>
		}

		if (richTextDescription) {
			return (
				<RichTextRenderer
					BlockMapper={BlockMapper}
					element={richTextDescription}
					globalConfig={globalConfig}
					page={page}
				/>
			)
		}

		if (selectedDescription && 'value' in selectedDescription) {
			return (
				<p className="whitespace-pre-line">
					{selectedDescription.value}
				</p>
			)
		}

		return null
	}

	return (
		<div
			className="pdp-header-container mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8"
			data-kontent-element-codename="pdp_header_block"
		>
			{/* Desktop Layout: Slider on left, content on right */}
			<div className="flex flex-col gap-4 lg:flex-row lg:gap-8 xl:gap-10">
				{/* Left Column - Sticky Slider */}
				{/* lg:!top-20 = 80px offset to account for fixed header height */}
				<div className="order-1 hidden w-full lg:sticky lg:!top-20 lg:block lg:h-fit lg:w-[55%] lg:!pt-4 lg:pr-4">
					{/* Slider for desktop */}
					{slider.length > 0 && slider[0] ? (
						<div data-kontent-element-codename="slider">
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'bottom',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								mainImageClassName="!px-4 md:!px-6"
								page={page}
								thumbnailClassName="p-1"
							/>
						</div>
					) : null}
				</div>

				{/* Right Column */}
				<div className="order-2 flex-1 space-y-6 lg:order-2">
					{/* Title */}
					{title ? (
						<h1
							className="text-gray-900 text-2xl font-bold lg:text-3xl"
							data-kontent-element-codename="title"
						>
							{title}
						</h1>
					) : null}

					{/* Product Buttons */}
					{(productLabelText && productLabelUrl) ||
					(safetyDataText && safetyDataUrl) ? (
						<div
							className="flex flex-col gap-3 sm:flex-row sm:gap-4"
							data-kontent-element-codename="product_label_text"
						>
							<div className="flex flex-wrap gap-3">
								{productLabelText && productLabelUrl ? (
									<TextButton
										className="!text-sm font-light not-italic"
										href={productLabelUrl}
										target="_blank"
										variant="none"
									>
										{productLabelText}
									</TextButton>
								) : null}
								{safetyDataUrl && safetyDataText ? (
									<TextButton
										className="ml-2 !text-sm font-light not-italic"
										href={safetyDataUrl}
										target="_blank"
										variant="none"
									>
										{safetyDataText}
									</TextButton>
								) : null}
							</div>
						</div>
					) : null}

					{/* Description (desktop only - shown below slider on mobile) */}
					{hasDescription && selectedDescription ? (
						<div
							className="text-gray-900 hidden text-base leading-relaxed lg:block lg:text-base"
							data-kontent-element-codename={
								richTextDescription
									? 'description_rich_text'
									: 'description'
							}
						>
							{renderDescriptionContent()}
						</div>
					) : null}

					{/* Ratings*/}
					{isUsLocale && ratings.length > 0 && ratings[0] ? (
						<div data-kontent-element-codename="ratings">
							<BazaarVoiceRatingsBlock
								BlockMapper={BlockMapper}
								block={ratings[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{/* PDP Popup Components - Rebate Available & Prescription Required buttons */}
					{(isUsLocale && pdpPopupRebates.length > 0) ||
					(isUsLocale && pdpPopupRichText.length > 0) ? (
						<div className="flex flex-wrap items-center gap-3">
							{pdpPopupRebates.length > 0 &&
							pdpPopupRebates[0] ? (
								<div data-kontent-element-codename="pdp_popup_rebates">
									<PDPPopupRebatesBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRebates[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
							{pdpPopupRichText.length > 0 &&
							pdpPopupRichText[0] ? (
								<div data-kontent-element-codename="pdp_popup_rich_text">
									<PDPPopupWithRichTextBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRichText[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
						</div>
					) : null}

					{/* Slider (MOBILE ONLY) */}
					{slider.length > 0 && slider[0] ? (
						<div
							className="lg:hidden"
							data-kontent-element-codename="slider"
						>
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'bottom',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								mainImageClassName="!px-8 sm:!px-10"
								page={page}
								thumbnailClassName="p-1"
							/>
						</div>
					) : null}

					{/* Description (mobile only - shown after slider) */}
					{hasDescription && selectedDescription ? (
						<div
							className="text-gray-900 text-base leading-relaxed lg:hidden"
							data-kontent-element-codename={
								richTextDescription
									? 'description_rich_text'
									: 'description'
							}
						>
							{renderDescriptionContent()}
						</div>
					) : null}

					{/* SKU Provider */}
					{isUsLocale && skuProvider.length > 0 && skuProvider[0] ? (
						<div data-kontent-element-codename="sku_provider">
							<SkuProviderBlock
								BlockMapper={BlockMapper}
								block={skuProvider[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{isUsLocale ? (
						<div className="flex flex-col gap-[20px]">
							<div>
								{pdpWithFindAVetCard.length > 0 &&
								pdpWithFindAVetCard[0] ? (
									<p className="text-[16px] font-black md:font-medium">
										{
											pdpWithFindAVetCard[0].elements
												.buyOrFindAVetCardTitle
										}
									</p>
								) : null}
							</div>
							<div className="flex flex-col gap-[16px] md:flex-row md:gap-[10px]">
								{pdpWithFindAVetCard.length > 0 &&
								pdpWithFindAVetCard[0] ? (
									<div
										className={`max-w-[328px] basis-4/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
										data-kontent-element-codename="pdp_with_find_a_vet_card"
									>
										<PDPFindAVetCard
											BlockMapper={BlockMapper}
											block={pdpWithFindAVetCard[0]}
											globalConfig={globalConfig}
											page={page}
										/>
									</div>
								) : null}
								{/* Pear Commerce Widget */}
								{pearCommerce.length > 0 && pearCommerce[0] ? (
									<div
										className={`max-w-[362px] basis-5/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
										data-kontent-element-codename="pear_commerce"
									>
										<PearCommerceWidget
											BlockMapper={BlockMapper}
											block={pearCommerce[0]}
											globalConfig={globalConfig}
											page={page}
										/>
									</div>
								) : null}
							</div>
						</div>
					) : null}

					{!isUsLocale ? (
						<div className="flex w-full flex-wrap items-center gap-3 md:w-auto md:gap-4">
							{buyOnlineCta.map((button) => (
								<div
									data-kontent-item-id={button.system.id}
									key={button.system.id}
								>
									<MikMakButtonBlock
										BlockMapper={BlockMapper}
										block={button}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							))}

							{findVetCta.map((button) => (
								<div
									data-kontent-item-id={button.system.id}
									key={button.system.id}
								>
									<ReusableCTAButtonComponent
										BlockMapper={BlockMapper}
										block={button}
										ctaButtonOrigin="pdp_header_cta"
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							))}
						</div>
					) : null}

					{/* PDP Header Accordion */}
					{pdpHeaderAccordion.length > 0 ? (
						<div
							className="w-full"
							data-kontent-element-codename="pdp_header_accordion"
						>
							{pdpHeaderAccordion.map((accordion) => (
								<PDPHeaderAccordionBlock
									BlockMapper={BlockMapper}
									block={accordion}
									globalConfig={globalConfig}
									key={accordion.system.id}
									page={page}
								/>
							))}
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}

export const PDPHeaderBlock = memo(PDPHeaderBlockComponent)
